import axios from "axios";
import beveatApiSandbox from "../../api/sandbox_beveat_api.js";
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { urls, commonConfigs } from "../../../config.js";
import ComingSoonModal from "../components/coming_soon_modal.jsx";
import HomeCard from "./components/developer_home_card.jsx";
import Rocket from "../../assets/icons/rocket.svg";
import CertificateMedal from "../../assets/icons/certificate_medal.svg";
import BrainstormSkill from "../../assets/icons/brainstorm_idea.svg";
import { DEVELOPER_PROJ_TABLE_SYSCODE } from "../../constants/developer_proj_table_syscode.jsx";
import { INVOICE_PLAN_SYSCODE } from "../../constants/invoice_plan_syscode.jsx";
import { INVOICE_SYSCODE } from "../../constants/invoice_syscode.jsx";
import CertificationUATFlow from "../../assets/diagrams/certification_uat_flow.png";
import CertificationGoLiveFlow from "../../assets/diagrams/certification_go_live_flow.png";
import CertificationUatGoLiveFlow from "../../assets/diagrams/certification_uat_go_live_flow.png";

const HomeLayout = () => {
  const [load, setLoad] = useState(true);
  const [data, setData] = useState([]);
  const [isOpen, setOpen] = useState(false);
  const [comingSoonDate, setComingSoonDate] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [showFAQ, setShowFAQ] = useState(false);
  const [zoomImage, setZoomImage] = useState(null);
  const [expandedFAQIndex, setExpandedFAQIndex] = useState(null);
  const navigate = useNavigate();
  const ssoId = localStorage.getItem("ssoId");
  const env = process.env.REACT_APP_ENV;
  const devProjTableSyscode = env === "production" ? DEVELOPER_PROJ_TABLE_SYSCODE.PRODUCTION : DEVELOPER_PROJ_TABLE_SYSCODE.STAGING;
  const getAllSandboxesUrl =
    urls.sandboxUrl + commonConfigs.apiUrls.GetAllDeveloperProjects(ssoId, devProjTableSyscode);

  // Add mobile detection
  useEffect(() => {
    const checkDevice = () => {
      const width = window.innerWidth;
      setIsMobile(width <= 1024); // Include tablets in mobile-friendly layout
    };

    checkDevice();
    window.addEventListener('resize', checkDevice);

    return () => window.removeEventListener('resize', checkDevice);
  }, []);

  async function getSandboxes() {
    await beveatApiSandbox
      .get(getAllSandboxesUrl, { headers: commonConfigs.sandboxApiHeader })
      .then((res) => {
        // console.log(res.data.result.data);
        // console.log(res.data.result.data.length);
        const sandboxProj = res.data.result.data.find((project) =>
          project.access.some((access) => access.project_name === "sandbox_nets")
        );
        setData(sandboxProj);
        setLoad(false);
      })
      .catch((err) => {
        console.log(err);
      });
  }

  const handleSandboxClick = () => {
    window.location.href = '/sandbox/create';
  };

  const handleComingSoonClick = (date) => {
    setComingSoonDate(date);
    setOpen(true);
  };

  const handleGetCertificationClick = () => {
    navigate("/pricing-plans", {
      state: {
        invoicePlanSyscode: INVOICE_PLAN_SYSCODE.Staging,
        invoiceSyscode: INVOICE_SYSCODE.Certification,
      }
    });
  };

  const handleGoLiveClick = () => {
    navigate("/pricing-plans", {
      state: {
        invoicePlanSyscode: INVOICE_PLAN_SYSCODE.Production,
        invoiceSyscode: INVOICE_SYSCODE.Certification,
      }
    });
  };

  useEffect(() => {
    getSandboxes();
  }, []);

  const faqList = [
    {
      question: "What is the difference between Sandbox, Staging (UAT) and Production (Go Live) projects?",
      answer: (
        <div className="text-left space-y-2">
          <p><strong>Sandbox</strong> is your initial testing environment. It is designed for experimentation and learning. It is ideal for testing out how APIs work.</p>
          <p><strong>Staging (UAT)</strong> stands for User Acceptance Testing. It simulates the real production environment closely and is meant for more serious development and certification. You will need to complete certification here before going live.</p>
          <p><strong>Production (Go Live)</strong> is the real deal. It connects to actual industry processes and transactions. You can only proceed to Production after getting certified in Staging (UAT).</p>
        </div>
      )
    },
    {
      question: "I am a beginner, where should I start with?",
      answer: (
        <div className="text-left space-y-2">
          <p>
            Start with the <strong>Sandbox environment</strong>. This environment is ideal for beginners to explore how APIs work without worrying about real transactions.
          </p>
          <p>
            Once you're confident and excited to move forward, you may proceed to create a <strong>Staging (UAT)</strong> project to prepare for real-world deployment and eventually Production (Go Live).
          </p>
        </div>
      )
    },
    {
      question: "Which developer project should I select when creating a Staging (UAT) project?",
      answer: (
        <div className="text-left space-y-2">
          <p><strong>If you are an intermediate developer</strong> who has just completed a Sandbox project and want to continue learning:</p>
          <ul className="list-disc list-inside ml-4 space-y-1">
            <li>
              Choose the <strong>IHLs-NETS-FinTech Curriculum</strong> or <strong>NETS Online Payments Coursework</strong> for guided training and a smooth transition into Production projects.
            </li>
            <li>
              <strong>IHLs-NETS-FinTech Curriculum</strong> is designed for students exploring specific industries (e.g., F&B, retail). It offers curated API combinations and guided coursework to help you get started quickly.
            </li>
            <li>
              <strong>NETS Online Payments Coursework</strong> is beginner-friendly and includes structured learning focused on how to use NETS APIs without having to build your own NETS merchant host server. This includes learning how to secure HMAC-SHA256 APIs effectively.
            </li>
          </ul>

          <p><strong>If you are an expert developer</strong> or ready to start your own custom project:</p>
          <ul className="list-disc list-inside ml-4 space-y-1">
            <li>
              Select the <strong>UAT Project Certification</strong> to build your own custom project by selecting industry APIs and NETS APIs you need.
            </li>
            <li>
              Select the <strong>UAT Payments Certification</strong> if you're already familiar with securing HMAC-SHA256 APIs and just want to use NETS APIs without having to build your own NETS merchant host server, and without going through coursework.
            </li>
          </ul>

          <img
            src={CertificationUATFlow}
            alt="Certification UAT project diagram"
            className="w-full max-w-xl mx-auto border border-gray-200 rounded-lg shadow-sm"
            onClick={() => setZoomImage(CertificationUATFlow)}
          />
        </div >
      )
    },
    {
      question: "Which developer project should I select when creating a Production (Go Live) project?",
      answer: (
        <div className="text-left space-y-2">
          <p><strong>UAT-Production Certification Pipeline: <br />
            If you are continuing from a certified UAT project</strong>, select the <strong>corresponding Production (Go Live) project type</strong>:</p>
          <ul className="list-disc list-inside ml-4 space-y-1">
            <li>
              If you completed <strong>IHLs-NETS-FinTech Curriculum</strong> or <strong>UAT Project Certification</strong>, continue with
              <strong> IHLs-NETS-FinTech Launchpads</strong> or <strong>Go Live Project Certification</strong> respectively.
            </li>
            <li>
              If you completed the <strong>NETS Online Payments Coursework</strong> or <strong>UAT Payments Certification</strong>, continue with
              <strong> Go Live Payments Certification</strong>.
            </li>
          </ul>

          <img
            src={CertificationGoLiveFlow}
            alt="Certification Go Live project diagram"
            className="w-full max-w-xl mx-auto border border-gray-200 rounded-lg shadow-sm"
            onClick={() => setZoomImage(CertificationGoLiveFlow)}
          />
        </div>
      )
    },
    {
      question: "Loaded script is disabled when I npm install in vscode, how do i fix it?",
      answer: (
        <div className="text-left space-y-2">
          <p>Run this command <strong>'Set-ExecutionPolicy -Scope CurrentUser -ExecutionPolicy RemoteSigned'</strong> in <strong>Windows PowerShell as Administrator</strong>.</p>
        </div>
      )
    },
    // {
    //   question: "How do I get started?",
    //   answer: (
    //     <div className="text-left space-y-2">
    //       <li>If you are a beginner, and want to explore NETS APIs, you may start with <strong>the Sandbox environment</strong> by downloading the Sandbox resources in the left navigation menu.</li>
    //       <li>If you <strong>require training</strong>, you may proceed to choose a <strong>IHLs-NETS-FinTech Curriculum</strong> by industry or <strong>NETS Online Payments Coursework</strong> in Staging (UAT).</li>
    //       <li>If you <strong>do not require training</strong>, you may proceed to choose the <strong>UAT Project Certification</strong> or <strong>UAT Payments Certification</strong> in Staging (UAT).</li>
    //       <p>
    //         Once you have selected your desired option, <strong>complete the certification process</strong> for your UAT project to ensure it is eligible for Production.
    //         After successful certification, you will need to proceed to the <strong>Certification (Go Live)</strong> tab, where you can choose the <strong>corresponding Production (Go Live) project type</strong> and <strong>select your certified UAT project</strong> from a dropdown list to proceed.
    //         If all the required conditions for the UAT project are met, your UAT project will be <strong>automatically certified and Go Live-ready</strong> without any additional actions required.
    //       </p>
    //       <img
    //         src={CertificationUatGoLiveFlow}
    //         alt="Production project creation steps"
    //         className="w-full max-w-xl mx-auto border border-gray-200 rounded-lg shadow-sm"
    //         onClick={() => setZoomImage(CertificationUatGoLiveFlow)}
    //       />
    //     </div>
    //   )
    // }
  ];


  if (!load) {
    return (
      <div className="w-full min-h-screen bg-gray-100">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-4">
          {/* Responsive header section */}
          <div
            className="text-center p-6 sm:p-8 lg:p-12 rounded-2xl relative mb-8"
            style={{
              backgroundColor: "#2b2d42",
              color: "white",
              boxShadow: "5px 10px #EBECF0"
            }}
          >
            <h1
              className={`mt-4 font-bold ${isMobile ? 'text-2xl sm:text-3xl' : 'text-4xl lg:text-5xl'
                }`}
            >
              Welcome Developers!
            </h1>
            <h3
              className={`mb-8 sm:mb-12 lg:mb-14 mt-4 font-medium ${isMobile ? 'text-lg sm:text-xl' : 'text-2xl lg:text-3xl'
                }`}
            >
              Empower Market Access And Social Mobility Today
            </h3>
          </div>

          {/* Responsive cards layout */}
          <div
            className={`flex relative ${isMobile
              ? 'flex-col gap-4 mt-8 items-center justify-center'
              : 'flex-row justify-around items-center'
              }`}
            style={{ marginTop: isMobile ? "0" : "-70px" }}
          >
            {data ? (
              <div className={isMobile ? "w-full max-w-sm mx-auto" : ""}>
                <HomeCard data={data} icon={BrainstormSkill} onClick={handleSandboxClick} />
              </div>
            ) : (
              <div className={isMobile ? "w-full max-w-sm mx-auto" : ""}>
                <HomeCard text="Explore NETS payments APIs" header="Sandbox" icon={BrainstormSkill} onClick={handleSandboxClick} />
              </div>
            )}

            <div className={isMobile ? "w-full max-w-sm mx-auto" : ""}>
              <HomeCard text="Get certified NETS-OPENAPIPAAS payments micro-credentials" header="UAT" icon={CertificateMedal} onClick={() => handleGetCertificationClick()} />
            </div>

            <div className={isMobile ? "w-full max-w-sm mx-auto" : ""}>
              <HomeCard text="Go live with NETS-OPENAPIPAAS industry payments APIs" header="Go Live" icon={Rocket} onClick={() => handleGoLiveClick()} />
            </div>
          </div>

          {/* Mobile-friendly spacing */}
          <div className="h-8 sm:h-12"></div>

          {isOpen && (
            <ComingSoonModal setOpenModal={setOpen} comingSoonDate={comingSoonDate} />
          )}

          {/* FAQ Section */}

          {/* Getting Started Guide (non-collapsible) */}
          <div className="mb-6 p-6 bg-white border border-blue-300 rounded-xl shadow-md">
            <h2 className="text-2xl font-bold text-blue-800 mb-4">How do I get started?</h2>
            <div className="text-left text-[16px] space-y-2 text-gray-800">
              <ul className="list-disc list-inside space-y-1">
                <li>
                  If you are a <strong>beginner</strong> and want to explore NETS APIs, you may start with <strong>the Sandbox environment</strong> by downloading the Sandbox resources in the left navigation menu.
                </li>
                <li>
                  If you <strong>require training</strong>, you may proceed to choose a <strong>IHLs-NETS-FinTech Curriculum</strong> by industry or <strong>NETS Online Payments Coursework</strong> in Staging (UAT).
                </li>
                <li>
                  If you <strong>do not require training</strong>, you may proceed to choose the <strong>UAT Project Certification</strong> or <strong>UAT Payments Certification</strong> in Staging (UAT).
                </li>
              </ul>
              <p>
                Once you have selected your desired option, <strong>complete the certification process</strong> for your UAT project to ensure it is eligible for Production.
                After successful certification, go to the <strong>Certification (Go Live)</strong> tab, where you can choose the <strong>corresponding Production (Go Live) project type</strong> and <strong>select your certified UAT project</strong> from a dropdown list to proceed.
              </p>
              <p>
                If all the required conditions for the UAT project are met, your UAT project will be <strong>automatically certified and Go Live</strong> without any additional actions required.
              </p>
              <br />
              <img
                src={CertificationUatGoLiveFlow}
                alt="Production project creation steps"
                className="w-[1000px] max-w-full mx-auto border border-gray-200 rounded-lg shadow-sm cursor-zoom-in"
                onClick={() => setZoomImage(CertificationUatGoLiveFlow)}
              />
            </div>
          </div>

          {zoomImage && (
            <div
              className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50"
              onClick={() => setZoomImage(null)}
            >
              <img
                src={zoomImage}
                alt="Zoomed"
                className="w-[90%] max-w-4xl max-h-[80vh] rounded-lg shadow-lg cursor-zoom-out object-contain"
              />
            </div>
          )}
          <div className="mb-10 p-6 bg-blue-50 border border-blue-200 rounded-xl shadow-sm">
            <h2 className="text-2xl font-bold text-blue-800 mb-4">Frequently Asked Questions</h2>
            <div className="space-y-3">
              {faqList.map((faq, index) => (
                <div key={index} className="border border-blue-200 bg-white rounded-lg p-4">
                  <button
                    onClick={() =>
                      setExpandedFAQIndex(expandedFAQIndex === index ? null : index)
                    }
                    className="w-full text-left flex justify-between items-center focus:outline-none"
                  >
                    <span className="text-lg font-semibold text-blue-800">
                      {faq.question}
                    </span>
                    <span className="text-blue-600 text-2xl font-bold">
                      {expandedFAQIndex === index ? "−" : "+"}
                    </span>
                  </button>
                  {expandedFAQIndex === index && (
                    <div className="mt-3 text-gray-700">{faq.answer}</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Add mobile-friendly loading state
  return (
    <div className="w-full min-h-screen bg-gray-100 flex items-center justify-center">
      <div className="text-center p-4">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
        <p className="text-gray-600">Loading...</p>
      </div>
    </div>
  );
};

export default HomeLayout;